/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { KeyNumber } from 'qp-navigation';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { ThumbnailData } from 'src/app/shared/models/ThumbnailData';
import { CuepointGroup } from '../../shared/models/PromoGroup';
import { trackByKey } from '../../shared/utils/trackBy';
import { Thumbnails } from '../interfaces/thumbnails.interface';
import { PlayerSeekbar } from './player-seekbar.interface';

@Component({
  selector: 'app-player-seekbar',
  templateUrl: './player-seekbar.component.html',
  styleUrls: ['./player-seekbar.component.scss'],
})
export class PlayerSeekbarComponent implements OnInit, OnDestroy {
  @ViewChild('bar') bar: ElementRef;
  @ViewChild('button') button: ElementRef;

  @Output() isSeeking = new EventEmitter<PlayerSeekbar>();
  @Output() endSeek = new EventEmitter<PlayerSeekbar>();
  @Output() enter = new EventEmitter<PlayerSeekbar>();
  @Input() max: number;
  @Input() value: number;
  @Input() step: number;
  @Input() isLive: boolean;
  @Input() cuepoints: CuepointGroup[];
  @Input() isAd = false;
  @Input() keyframesEnabled = false;
  @Input() showCuepoints = false;
  @Input() thumbnails: Thumbnails;
  @Input() automaticSeek: boolean;
  @Input() blockControls = false;
  @Input() percentage = 0;
  @Input() speedInfo: { direction: -1 | 1; speed: string };

  currentThumbnail: ThumbnailData;
  seeking = false;
  seekingWithMouse = false;
  innerValue: number;
  trackByStartTime = trackByKey<CuepointGroup, 'startTime'>('startTime');

  // private seekInterval: any = null;
  // private lastKeyTime = 0;
  private virtualValue = 0;
  private destroy$ = new Subject<void>();
  private keyDown$ = new Subject<KeyboardEvent>();
  private keyUp$ = new Subject<void>();
  private offsetLeft = 0;
  private animationFrameId: number | null = null;
  private lastKeyCode: number | null = null;
  private lastEmittedValue = 0;
  private isEmitting = false;

  // Optimization: Reuse a single object for emissions to reduce GC pressure
  private readonly seekEventObject: PlayerSeekbar = { value: 0 };

  // Used for FPS throttling
  private lastFrameTime = 0;
  private readonly targetFPS = 60;
  private readonly frameDuration = 1000 / 60; // ~16.67ms for 60fps

  constructor(private renderer: Renderer2, private el: ElementRef, private ngZone: NgZone) {}

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.setupKeyboardEvents();
      this.setupMouseEvents();
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    // Controls are blocked when the player is in an ad state or buffering
    if (this.blockControls) return;
    if ([KeyNumber.LEFT, KeyNumber.RIGHT].includes(event.keyCode)) {
      event.preventDefault();
      this.keyDown$.next(event);
    } else if (KeyNumber.ENTER === event.keyCode) {
      this.ngZone.run(() => {
        this.enter.emit();
      });
    }
  }

  onMouseDown(): void {
    if (this.blockControls) return;
    this.ngZone.runOutsideAngular(() => {
      this.renderer.addClass(this.el.nativeElement, 'transition');
      this.offsetLeft = this.getOffsetLeft(this.el.nativeElement);
      this.seekingWithMouse = true;
      // Reuse the event object
      this.seekEventObject.value = this.value;
      this.ngZone.run(() => {
        this.isSeeking.emit(this.seekEventObject);
      });
    });
    this.isSeeking.emit({ value: this.value });
  }

  onFocusOut() {
    if (this.seeking) {
      this.handleChange();
    }
  }

  ngOnDestroy() {
    this.stopAnimationFrame();
    this.destroy$.next();
    this.destroy$.complete();
    this.keyDown$.complete();
    this.keyUp$.complete();
  }

  clampPercentage(value: number): number {
    return Math.max(0, Math.min(100, value));
  }

  private setupKeyboardEvents(): void {
    this.keyDown$
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.blockControls),
        tap((event) => {
          if (!this.seeking) {
            this.seeking = true;
            this.virtualValue = this.value;
            this.lastKeyCode = event.keyCode;
            this.startAnimationLoop();
          } else {
            this.lastKeyCode = event.keyCode;
          }
        })
      )
      .subscribe();
    merge(
      fromEvent<KeyboardEvent>(document, 'keyup').pipe(
        filter((event: KeyboardEvent) => [KeyNumber.LEFT, KeyNumber.RIGHT].includes(event.keyCode))
      ),
      this.keyUp$
    )
      .pipe(takeUntil(this.destroy$), debounceTime(50))
      .subscribe(() => {
        if (this.seeking) {
          this.handleChange();
        }
      });
  }

  private startAnimationLoop(): void {
    if (this.animationFrameId) {
      return;
    }

    this.lastFrameTime = performance.now();

    const step = (timestamp: number) => {
      const elapsed = timestamp - this.lastFrameTime;

      if (elapsed > this.frameDuration) {
        this.lastFrameTime = timestamp - (elapsed % this.frameDuration);

        if (this.lastKeyCode !== null) {
          const sign = KeyNumber.RIGHT === this.lastKeyCode ? +1 : -1;
          this.virtualValue = Math.min(Math.max(0, this.virtualValue + sign * this.step), this.max - 1);
          this.innerValue = this.virtualValue;

          if (!this.isEmitting && Math.abs(this.lastEmittedValue - this.virtualValue) > this.step) {
            this.isEmitting = true;
            this.lastEmittedValue = this.virtualValue;

            // Reuse the event object
            this.seekEventObject.value = this.virtualValue;
            this.seekEventObject.keyCode = this.lastKeyCode;

            this.ngZone.run(() => {
              this.isSeeking.emit(this.seekEventObject);
              this.handleThumbnail();
              this.isEmitting = false;
            });
          }
        }
      }
      this.animationFrameId = requestAnimationFrame(step);
    };

    this.animationFrameId = requestAnimationFrame(step);
  }

  private stopAnimationFrame(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private setupMouseEvents(): void {
    // Click event
    fromEvent(this.el.nativeElement, 'click')
      .pipe(
        takeUntil(this.destroy$),
        filter((e: any) => {
          const className = e?.srcElement?.className || '';
          return !className.includes('button');
        })
      )
      .subscribe((e: any) => {
        if (this.blockControls) return;
        this.offsetLeft = this.getOffsetLeft(this.el.nativeElement);
        const x = e.clientX - this.offsetLeft;
        const width = this.bar.nativeElement.offsetWidth;
        const value = (x / width) * this.max;
        this.value = value;
        this.endSeek.emit({ value });
        // Reuse the event object
        this.seekEventObject.value = value;
        this.ngZone.run(() => {
          this.endSeek.emit(this.seekEventObject);
        });
      });
    // Mouse up event
    fromEvent(document, 'mouseup', { capture: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.seekingWithMouse) {
          this.seekingWithMouse = false;
          this.renderer.removeClass(this.el.nativeElement, 'transition');

          // Reuse the event object
          this.seekEventObject.value = this.value;
          this.ngZone.run(() => {
            this.endSeek.emit(this.seekEventObject);
          });
        }
      });

    // Mouse move event
    fromEvent(document, 'mousemove')
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.seekingWithMouse),
        throttleTime(1000 / this.targetFPS)
      )
      .subscribe((e: any) => {
        if (!this.bar?.nativeElement) return;

        const x = e.clientX - this.offsetLeft;
        const width = this.bar.nativeElement.offsetWidth;
        const value = (x / width) * this.max;

        this.value = Math.min(Math.max(0, value), this.max);

        if (!this.isEmitting) {
          this.isEmitting = true;

          // Reuse the event object
          this.seekEventObject.value = this.value;

          this.ngZone.run(() => {
            this.isSeeking.emit(this.seekEventObject);
            this.handleThumbnail();
            this.isEmitting = false;
          });
        }
      });
  }

  private handleThumbnail() {
    if (!this.keyframesEnabled || !this.thumbnails?.content?.length) return;

    // Optimization: Reduce array operations
    let closest = this.thumbnails.content[0];
    let minDiff = Math.abs(closest.id - this.innerValue);

    for (let i = 1; i < this.thumbnails.content.length; i++) {
      const diff = Math.abs(this.thumbnails.content[i].id - this.innerValue);
      if (diff < minDiff) {
        minDiff = diff;
        closest = this.thumbnails.content[i];
      }
    }

    this.currentThumbnail = closest;
  }

  private handleChange(): void {
    this.stopAnimationFrame();
    this.lastKeyCode = null;

    // Reuse the event object
    this.seekEventObject.value = this.innerValue;

    this.ngZone.run(() => {
      this.endSeek.emit(this.seekEventObject);
      this.seeking = false;
      this.renderer.removeClass(this.el.nativeElement, 'transition');
    });
  }

  private getOffsetLeft(el: any): number {
    let offset = 0;
    while (el) {
      offset += el.offsetLeft || 0;
      el = el.offsetParent;
    }
    return offset;
  }
}
